import styled from '@emotion/styled';
import { Modal, tokens, Typography } from '@sunrun/experience-ui-components';

const INCIDENT_EMAIL = 'incident@sunrun.com';

const REPORT_ITEMS = [
  {
    label: 'Customer info',
    text: 'Name and address/market',
  },
  {
    label: 'Who was involved',
    text: 'Company name, rep name, phone, and/or email',
  },
  {
    label: 'The details',
    text: 'Date/time and a short recap of what was said/done',
  },
  {
    label: 'Proof',
    text: 'Full screenshots of texts, flyers, cancellation forms, or emails',
  },
] as const;

type ReportPoachingModalProps = {
  onClose: () => void;
};

const ReportPoachingModal = ({ onClose }: ReportPoachingModalProps) => (
  <StyledModal
    testId="report-poaching-modal"
    customModalWidth={1000}
    type="info"
    title="Reporting Suspected Customer Poaching"
    onClose={onClose}
    primaryButton={{
      text: 'Acknowledge',
      onClick: onClose,
      testId: 'report-poaching-modal-acknowledge',
    }}>
    <Body>
      <Paragraph color={tokens.TINTS_OFF_WHITE_20}>
        Protecting our sales is a priority, and we need everyone&apos;s help
        identifying and reporting potential poaching activity.
      </Paragraph>
      <Paragraph color={tokens.TINTS_OFF_WHITE_20}>
        If someone impersonates Sunrun, makes misleading claims about Sunrun, or
        uses deceptive tactics to persuade a customer to cancel or switch
        service,{' '}
        <Emphasis>
          report it immediately to{' '}
          <EmailLink href={`mailto:${INCIDENT_EMAIL}`}>
            {INCIDENT_EMAIL}
          </EmailLink>
        </Emphasis>
        .
      </Paragraph>
      <Paragraph color={tokens.TINTS_OFF_WHITE_20}>
        <Emphasis>Include what you have:</Emphasis>
      </Paragraph>
      <List>
        {REPORT_ITEMS.map(({ label, text }) => (
          <li key={label}>
            <Emphasis>{label}:</Emphasis> {text}
          </li>
        ))}
      </List>
      <Paragraph color={tokens.TINTS_OFF_WHITE_20}>
        <Emphasis>Don’t wait for all the details.</Emphasis>
      </Paragraph>
      <Paragraph color={tokens.TINTS_OFF_WHITE_20}>
        <Emphasis>When in doubt, report it.</Emphasis> Sunrun will investigate
        and determine next steps.
      </Paragraph>
    </Body>
  </StyledModal>
);

const StyledModal = styled(Modal)`
  .modal-container {
    max-width: calc(100vw - 40px);
  }

  .modal-content-container {
    padding-top: 0;
    padding-bottom: 0;
    max-height: unset;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Paragraph = styled(Typography)`
  && {
    margin: 0;
    font-size: 18px;
    line-height: unset;
  }
`;

const Emphasis = styled.span`
  font-weight: 500;
`;

const EmailLink = styled.a`
  color: ${tokens.BRAND_HEROBLUE};
  font-weight: 500;
  text-decoration: underline;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25em;
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 18px;
  line-height: 1;
  letter-spacing: -0.4px;

  li + li {
    margin-top: 8px;
  }
`;

export { ReportPoachingModal };
